// CompanyClustersPanel.tsx
import { useSigma } from "@react-sigma/core";
import { keyBy, mapValues, sortBy, values } from "lodash";
import { FC, useEffect, useMemo, useState } from "react";
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from "react-icons/ai";
import { MdBusinessCenter } from "react-icons/md";

import { Cluster, FiltersState } from "../types";
import Panel from "./Panel";

const COMPANY_CLUSTER_COLOR = "#333333"; // Light black color for company clusters

const CompanyClustersPanel: FC<{
  clusters: Cluster[];
  filters: FiltersState;
  toggleCluster: (cluster: string) => void;
  setClusters: (clusters: Record<string, boolean>) => void;
}> = ({ clusters, filters, toggleCluster, setClusters }) => {
  const sigma = useSigma();
  const graph = sigma.getGraph();

  const nodesPerCluster = useMemo(() => {
    const index: Record<string, number> = {};
    graph.forEachNode((_, attributes) => {
      attributes.companyCluster.forEach((cluster: string) => {
        index[cluster] = (index[cluster] || 0) + 1;
      });
    });
    return index;
  }, [graph]);

  const maxNodesPerCluster = useMemo(() => Math.max(...values(nodesPerCluster)), [nodesPerCluster]);
  const visibleClustersCount = useMemo(() => 
    Object.keys(filters.clusters).filter(key => key.startsWith('company-') && filters.clusters[key]).length, 
    [filters]
  );

  const [visibleNodesPerCluster, setVisibleNodesPerCluster] = useState<Record<string, number>>(nodesPerCluster);
  useEffect(() => {
    requestAnimationFrame(() => {
      const index: Record<string, number> = {};
      graph.forEachNode((_, attributes) => {
        if (!attributes.hidden) {
          attributes.companyCluster.forEach((cluster: string) => {
            index[cluster] = (index[cluster] || 0) + 1;
          });
        }
      });
      setVisibleNodesPerCluster(index);
    });
  }, [filters, graph]);

  const sortedClusters = useMemo(
    () => sortBy(clusters, (cluster) => -nodesPerCluster[cluster.key]),
    [clusters, nodesPerCluster],
  );

  return (
    <Panel
      title={
        <>
          <MdBusinessCenter className="text-muted" /> Company Clusters
          {visibleClustersCount < clusters.length ? (
            <span className="text-muted text-small">
              {" "}
              ({visibleClustersCount} / {clusters.length})
            </span>
          ) : (
            ""
          )}
        </>
      }
    >
      <p>
        <i className="text-muted">Click a cluster to show/hide related pages from the network.</i>
      </p>
      <p className="buttons">
        <button className="btn" onClick={() => setClusters(mapValues(keyBy(clusters, "key"), () => true))}>
          <AiOutlineCheckCircle /> Check all
        </button>{" "}
        <button className="btn" onClick={() => setClusters(mapValues(keyBy(clusters, "key"), () => false))}>
          <AiOutlineCloseCircle /> Uncheck all
        </button>
      </p>
      <ul>
        {sortedClusters.map((cluster) => {
          const nodesCount = nodesPerCluster[cluster.key] || 0;
          const visibleNodesCount = visibleNodesPerCluster[cluster.key] || 0;
          return (
            <li
              className="caption-row"
              key={cluster.key}
              title={`${nodesCount} page${nodesCount > 1 ? "s" : ""}${
                visibleNodesCount !== nodesCount ? ` (only ${visibleNodesCount} visible)` : ""
              }`}
            >
              <input
                type="checkbox"
                checked={filters.clusters[`company-${cluster.key}`] || false}
                onChange={() => toggleCluster(`company-${cluster.key}`)}
                id={`company-cluster-${cluster.key}`}
              />
              <label htmlFor={`company-cluster-${cluster.key}`}>
                <span className="circle" style={{ background: COMPANY_CLUSTER_COLOR, borderColor: COMPANY_CLUSTER_COLOR}} />{" "}
                <div className="node-label">
                  <span>{cluster.clusterLabel}</span>
                  <div className="bar" style={{ width: (100 * nodesCount) / maxNodesPerCluster + "%" }}>
                    <div
                      className="inside-bar"
                      style={{
                        width: (100 * visibleNodesCount) / nodesCount + "%",
                      }}
                    />
                  </div>
                </div>
              </label>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
};

export default CompanyClustersPanel;
